import type { Inning, MatchResult, ResultMode, RuleSet, SetScore, Tiebreaker, Validation } from '../types.js';

const RESULT_MODES: readonly string[] = ['score', 'winnerOnly'] satisfies ResultMode[];
const TIEBREAKERS: readonly string[] = ['points', 'headToHead', 'scoreDiff', 'scoreFor', 'wins', 'lot'] satisfies Tiebreaker[];

/**
 * Regelwerk (oder Patch/Override) auf die heute möglichen Werte bringen.
 * Zwei Optionen gab es einmal in der Auswahl, ohne dass etwas dahinterstand:
 * `resultMode: 'innings'` („Inning für Inning") und der Tiebreaker
 * `buchholz` (ohne Schweizer System). Gespeicherte Protokolle tragen sie
 * noch — beim Anlegen und Ändern eines Regelwerks werden sie hier auf das
 * Nächstliegende gesetzt: Endstand bzw. Kriterium weglassen. Alles andere
 * bleibt, wie es ist (auch die Reihenfolge der Felder).
 */
export function normalizeRules<T extends Partial<RuleSet>>(rules: T): T {
  const out = { ...rules };
  if (out.resultMode !== undefined && !RESULT_MODES.includes(out.resultMode)) out.resultMode = 'score';
  if (out.tiebreakers) out.tiebreakers = out.tiebreakers.filter((c) => TIEBREAKERS.includes(c));
  return out;
}

/**
 * Prüft ein Ergebnis gegen das Regelwerk (docs/02).
 *
 * Philosophie aus docs/06 §9: Das Tool verbietet der Turnierleitung nichts, es
 * warnt nur. Deshalb sind fast alle Verstöße `warnings` (mit `force`
 * übersteuerbar); `errors` sind nur strukturell unmögliche Eingaben.
 */
export function validateResult(rules: RuleSet, result: MatchResult, ctx: { drawsAllowed: boolean }): Validation {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (result.sets.length === 0 && result.endedBy !== 'walkover' && result.endedBy !== 'forfeit') {
    errors.push('Kein Satz eingetragen');
    return { ok: false, errors, warnings };
  }

  for (const [i, set] of result.sets.entries()) {
    const label = result.sets.length > 1 ? `Satz ${i + 1}: ` : '';
    if (!Number.isInteger(set.a) || !Number.isInteger(set.b) || set.a < 0 || set.b < 0) {
      errors.push(`${label}Punkte müssen ganze Zahlen ≥ 0 sein`);
      continue;
    }
    if (set.innings) {
      const calc = scoreFromInnings(set.innings, rules);
      if (calc.a !== set.a || calc.b !== set.b) {
        errors.push(`${label}Innings ergeben ${calc.a}:${calc.b}, eingetragen ist ${set.a}:${set.b}`);
      }
      if (rules.maxInnings && set.innings.length > rules.maxInnings) {
        warnings.push(`${label}${set.innings.length} Innings, erlaubt sind ${rules.maxInnings}`);
      }
    }
    if (result.endedBy === 'target') warnings.push(...checkSetAtTarget(rules, set, label));
    if (result.endedBy === 'limit' && !rules.maxInnings && !rules.timeLimitMin) {
      warnings.push(`${label}Abbruch am Limit, aber es ist kein Limit konfiguriert`);
    }
    if (result.endedBy === 'draw' && set.a !== set.b) warnings.push(`${label}Unentschieden mit ungleichem Stand`);
    if (result.endedBy === 'draw' && !ctx.drawsAllowed) warnings.push(`${label}Unentschieden ist in dieser Phase nicht erlaubt`);
  }

  const expectedWinner = winnerFromSets(rules, result.sets);
  if (result.endedBy !== 'walkover' && result.endedBy !== 'forfeit') {
    if (expectedWinner === null && result.winner !== null) {
      warnings.push('Eingetragener Sieger passt nicht zu den Sätzen (Gleichstand)');
    } else if (expectedWinner !== null && result.winner !== expectedWinner) {
      warnings.push(`Eingetragener Sieger passt nicht zu den Sätzen (rechnerisch ${expectedWinner})`);
    }
  }
  if (result.winner === null && !ctx.drawsAllowed && result.endedBy !== 'walkover') {
    warnings.push('Kein Sieger, aber Unentschieden ist nicht erlaubt');
  }

  const fmt = rules.matchFormat;
  if (fmt.kind === 'single' && result.sets.length > 1) warnings.push('Mehr als ein Satz bei Einzelspiel');
  if (fmt.kind === 'bestOf') {
    const need = Math.ceil(fmt.sets / 2);
    const won = setWins(result.sets);
    if (result.endedBy === 'target' && won.a < need && won.b < need) {
      warnings.push(`Best-of-${fmt.sets}: kein Team hat ${need} Sätze gewonnen`);
    }
    if (result.sets.length > fmt.sets) warnings.push(`Mehr als ${fmt.sets} Sätze`);
  }
  if (fmt.kind === 'fixedSets' && result.sets.length !== fmt.sets && result.endedBy === 'target') {
    warnings.push(`Es müssen genau ${fmt.sets} Sätze gespielt werden`);
  }

  return { ok: errors.length === 0, errors, warnings };
}

function checkSetAtTarget(rules: RuleSet, set: SetScore, label: string): string[] {
  const w: string[] = [];
  const hi = Math.max(set.a, set.b);
  const lo = Math.min(set.a, set.b);
  const diff = hi - lo;
  if (hi < rules.target) w.push(`${label}Punktziel ${rules.target} nicht erreicht`);
  if (rules.bust !== 'off' && hi > rules.target) w.push(`${label}Mit Bust-Regel kann niemand über ${rules.target} kommen`);
  if (rules.winBy === 1) {
    if (lo >= rules.target) w.push(`${label}Beide Teams über dem Punktziel`);
  } else {
    if (rules.cap && hi >= rules.cap) {
      if (diff < 1) w.push(`${label}Deckel erreicht, aber kein Vorsprung`);
    } else if (diff < 2) {
      w.push(`${label}Sieg nur mit 2 Punkten Vorsprung`);
    }
    if (rules.cap && hi > rules.cap) w.push(`${label}Über dem Deckel ${rules.cap}`);
  }
  return w;
}

/** Cancellation Scoring: je Inning zählt nur die Differenz. Bust optional. */
export function scoreFromInnings(innings: Inning[], rules: RuleSet): { a: number; b: number } {
  let a = 0;
  let b = 0;
  for (const inn of innings) {
    const ra = inn.a.hole * 3 + inn.a.board;
    const rb = inn.b.hole * 3 + inn.b.board;
    if (ra > rb) a = applyBust(a + (ra - rb), rules);
    else if (rb > ra) b = applyBust(b + (rb - ra), rules);
  }
  return { a, b };
}

function applyBust(score: number, rules: RuleSet): number {
  if (rules.bust !== 'off' && score > rules.target) return rules.bust;
  return score;
}

export function setWins(sets: SetScore[]): { a: number; b: number } {
  let a = 0;
  let b = 0;
  for (const s of sets) {
    if (s.a > s.b) a++;
    else if (s.b > s.a) b++;
  }
  return { a, b };
}

/** Rechnerischer Sieger aus den Sätzen; null bei Gleichstand */
export function winnerFromSets(rules: RuleSet, sets: SetScore[]): 'A' | 'B' | null {
  if (rules.matchFormat.kind === 'single') {
    const s = sets[0];
    if (!s) return null;
    return s.a > s.b ? 'A' : s.b > s.a ? 'B' : null;
  }
  const w = setWins(sets);
  return w.a > w.b ? 'A' : w.b > w.a ? 'B' : null;
}

/** Ergebnis für Walkover (Nicht-Antreten) nach Regelwerk */
export function walkoverResult(rules: RuleSet, winner: 'A' | 'B'): MatchResult {
  const setsNeeded = rules.matchFormat.kind === 'bestOf' ? Math.ceil(rules.matchFormat.sets / 2) : rules.matchFormat.kind === 'fixedSets' ? rules.matchFormat.sets : 1;
  const sets: SetScore[] = [];
  for (let i = 0; i < setsNeeded; i++) {
    sets.push(winner === 'A' ? { a: rules.walkover.winner, b: rules.walkover.loser } : { a: rules.walkover.loser, b: rules.walkover.winner });
  }
  return { sets, endedBy: 'walkover', winner };
}

/** Aufgabe: Gegner bekommt Punktziel, Aufgebender behält seinen Stand */
export function forfeitResult(rules: RuleSet, current: SetScore[], quitter: 'A' | 'B'): MatchResult {
  const sets = current.map((s) => ({ ...s }));
  const last = sets[sets.length - 1] ?? { a: 0, b: 0 };
  if (sets.length === 0) sets.push(last);
  if (quitter === 'A') last.b = Math.max(last.b, rules.target);
  else last.a = Math.max(last.a, rules.target);
  sets[sets.length - 1] = last;
  return { sets, endedBy: 'forfeit', winner: quitter === 'A' ? 'B' : 'A' };
}
